import React, { SyntheticEvent, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useLocales from 'hooks/useLocales';
import './ChartDescription.scss';

export interface ChartPoint {
  point: number | string;
  label: string;
  descriptions?: string;
  comparePoint?: number | string | null;
}

interface ChartDescriptionProps {
  labels: ChartPoint[];
  accordionExpanded?: boolean;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  hideHeader?: boolean;
}

const AccordionTitle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
}));

const ButtonAccordionSummary = styled(AccordionSummary)(() => ({
  background: 'linear-gradient(92.25deg, #9B5FDA -2.57%, #562C82 113.38%)',
  boxShadow: '0px 0px 6.51128px rgba(0, 0, 0, 0.08)',
  borderRadius: '13px'
}));

const ButtonTypography = styled(Typography)`
  font-family: Rubik, serif;
  font-weight: 600 !important;
  font-size: 14px !important;
  line-height: 17px !important;
  color: #ffffff;
`;

const AccordionCard = styled(Accordion)`
  border-radius: 15px !important;
  box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.08) !important;
  margin: 16px 0 !important;

  &:before {
    display: none;
  }

  &:nth-child(2) {
    margin: 0 !important;

    .MuiButtonBase-root {
      min-height: 48px;
      height: 48px;
    }
  }
`;

const LabelText = styled(Typography)`
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
  text-decoration-color: rgba(0, 0, 0, 0.3);
`;

const ChartDescription: React.FC<ChartDescriptionProps> = ({
  labels,
  accordionExpanded = true,
  style,
  onClick,
  hideHeader
}) => {
  const { t } = useLocales();
  const [expanded, setExpanded] = useState(accordionExpanded);

  // Convert point to number for calculations
  const getPointValue = (point: number | string): number => {
    if (typeof point === 'string') {
      const numValue = parseFloat(point);
      return isNaN(numValue) ? 0 : numValue;
    }
    return point;
  };

  // Calculate percentage for progress bar
  const getPercentage = (point: number | string): string => {
    const numValue = getPointValue(point);
    // Assume max value is 10, adjust if needed
    const maxValue = 10;
    return `${(numValue / maxValue) * 100}%`;
  };

  // Check if we have any valid candidate data to display
  const hasValidCandidateData = labels?.some(
    (label) => label.comparePoint !== null && label.comparePoint !== undefined
  );

  const handleChange = (
    event: SyntheticEvent<Element, Event>,
    isExpanded: boolean
  ) => {
    // Stop event propagation to prevent modal from closing
    if (event) {
      event.stopPropagation();
    }
    setExpanded(isExpanded);
  };

  const handleContentClick = (e: React.MouseEvent) => {
    // Stop propagation for any clicks inside the accordion content
    e.stopPropagation();
    if (onClick) onClick(e);
  };

  return labels && labels.length ? (
    <AccordionCard
      style={style}
      expanded={expanded}
      onChange={handleChange}
      onClick={handleContentClick}
    >
      <ButtonAccordionSummary
        expandIcon={<ExpandMoreIcon htmlColor="#FFFFFF" />}
        style={{
          height: expanded ? '64px' : '48px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <AccordionTitle>
          <ButtonTypography variant="h6">{t('Description')}</ButtonTypography>
        </AccordionTitle>
      </ButtonAccordionSummary>
      <AccordionDetails onClick={handleContentClick}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          gap={2}
          my={2}
          pr="18px"
          width="100%"
        >
          {!hideHeader && (
            <Box display="flex" alignItems="center">
              <span className="dot purple"></span>
              <Typography variant="caption" color="#562C82" fontWeight={600}>
                {t('Job')}
              </Typography>
            </Box>
          )}
          {hasValidCandidateData && !hideHeader && (
            <Box display="flex" alignItems="center">
              <span className="dot blue"></span>
              <Typography variant="caption" color="#1C9DD7" fontWeight={600}>
                {t('Candidate')}
              </Typography>
            </Box>
          )}
        </Box>

        {labels.map(({ point, label, descriptions, comparePoint }, i) => (
          <div key={i}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: '44%' },
                  textAlign: 'left'
                }}
              >
                {descriptions ? (
                  <Tooltip
                    title={descriptions}
                    arrow
                    placement="top-start"
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                  >
                    <LabelText variant="body2" fontWeight={500}>
                      {`${i + 1}. ${label}`}
                    </LabelText>
                  </Tooltip>
                ) : (
                  <Typography variant="body2" fontWeight={500}>
                    {`${i + 1}. ${label}`}
                  </Typography>
                )}
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '55%' } }}>
                <div className="progress-container">
                  <div className="progress-label">
                    <span className="dot purple"></span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill purple"
                      style={{ width: getPercentage(point) }}
                    ></div>
                  </div>
                  <span className="progress-value">{point}</span>
                </div>
                {comparePoint !== null && comparePoint !== undefined && (
                  <div className="progress-container">
                    <div className="progress-label">
                      <span className="dot blue"></span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill blue"
                        style={{ width: getPercentage(comparePoint) }}
                      ></div>
                    </div>
                    <span className="progress-value">{comparePoint}</span>
                  </div>
                )}
              </Box>
            </Box>
          </div>
        ))}
      </AccordionDetails>
    </AccordionCard>
  ) : null;
};

export default ChartDescription;
